import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import {
  getMe,
  enableMFA,
  disableMFA,
  regenerateBackupCodes,
  type MFAEnableResponse,
} from "../services/authService";
import { Footer } from "../components/layout/Footer";

export function ProfilePage() {
  const { user, setAuth, accessToken, refreshToken, expiresAt } =
    useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // MFA Enable flow
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<"totp" | "email">("totp");
  const [mfaData, setMfaData] = useState<MFAEnableResponse | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // MFA Disable flow
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");

  // Regenerate backup codes
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState("");

  // Refresh user data on mount
  useEffect(() => {
    const refreshUser = async () => {
      try {
        const freshUser = await getMe();
        if (accessToken && refreshToken && expiresAt) {
          setAuth(accessToken, refreshToken, expiresAt, freshUser);
        }
      } catch {
        // User will see stale data, which is fine
      }
    };
    refreshUser();
  }, []);

  const handleEnableMFA = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await enableMFA(mfaMethod);
      setMfaData(response);
      setShowMFASetup(false);
      setShowBackupCodes(true);
      setSuccess("MFA enabled successfully! Save your backup codes.");

      // Refresh user data
      const freshUser = await getMe();
      if (accessToken && refreshToken && expiresAt) {
        setAuth(accessToken, refreshToken, expiresAt, freshUser);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable MFA");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!disablePassword) {
      setError("Password is required to disable MFA");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await disableMFA(disablePassword);
      setShowDisableConfirm(false);
      setDisablePassword("");
      setSuccess("MFA disabled successfully");
      setMfaData(null);

      // Refresh user data
      const freshUser = await getMe();
      if (accessToken && refreshToken && expiresAt) {
        setAuth(accessToken, refreshToken, expiresAt, freshUser);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable MFA");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regeneratePassword) {
      setError("Password is required to regenerate backup codes");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await regenerateBackupCodes(regeneratePassword);
      setMfaData({ backup_codes: response.backup_codes });
      setShowRegenerateConfirm(false);
      setRegeneratePassword("");
      setShowBackupCodes(true);
      setSuccess("New backup codes generated! Old codes are now invalid.");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to regenerate backup codes",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess("Copied to clipboard!");
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <div className="flex flex-col">
    <div className="flex-1 flex animated-gradient">
      {/* Left side - Branding/Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/header.mp4?v=2" type="video/mp4" />
        </video>

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12">
          {/* Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
          >
            <img src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8" />
          </a>

          {/* TradeCraft branding */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-16 h-16 drop-shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold gradient-text">
                  TradeCraft
                </h1>
                <p className="text-surface-400 text-lg">Algo Builder</p>
              </div>
            </div>
          </div>

          {/* Security features */}
          <div className="space-y-4">
            {[
              {
                icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
                title: "Multi-Factor Authentication",
                desc: "Protect your account with TOTP or Email OTP",
              },
              {
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                title: "Secure Sessions",
                desc: "JWT tokens with short expiry and refresh",
              },
              {
                icon: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
                title: "Backup Codes",
                desc: "Recovery codes for account access",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={feature.icon}
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-surface-100">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-surface-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      {/* Right side - Profile */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/colvix_header_final.png"
                alt="Colvix"
                className="colvix-logo h-6 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-12 h-12" />
              <h1 className="text-2xl font-bold gradient-text">
                TradeCraft
              </h1>
            </div>
            <p className="text-surface-400">Algo Builder</p>
          </div>

          {/* Profile card */}
          <div className="bg-surface-900/50 backdrop-blur-lg rounded-2xl p-8 border border-surface-700/50 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {user?.display_name?.charAt(0).toUpperCase() ||
                    user?.email?.charAt(0).toUpperCase() ||
                    "U"}
                </span>
              </div>
              <h2 className="text-2xl font-bold text-surface-100 mb-1">
                {user?.display_name || "User"}
              </h2>
              <p className="text-surface-400">{user?.email}</p>
            </div>

            {/* Status badges */}
            <div className="flex justify-center gap-3 mb-8">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user?.email_verified
                    ? "bg-green-500/20 text-green-400"
                    : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                {user?.email_verified
                  ? "✓ Email Verified"
                  : "⚠ Email Not Verified"}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user?.mfa_enabled
                    ? "bg-green-500/20 text-green-400"
                    : "bg-surface-600/50 text-surface-400"
                }`}
              >
                {user?.mfa_enabled
                  ? `✓ MFA (${user.mfa_method})`
                  : "MFA Disabled"}
              </span>
            </div>

            {/* Error/Success messages */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                <p className="text-sm text-green-400">{success}</p>
              </div>
            )}

            {/* MFA Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                Two-Factor Authentication
              </h3>

              {!user?.mfa_enabled ? (
                // MFA not enabled - show enable option
                !showMFASetup ? (
                  <button
                    onClick={() => setShowMFASetup(true)}
                    className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Enable MFA
                  </button>
                ) : (
                  // MFA setup form
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-surface-800/50 border border-surface-700/50">
                      <p className="text-sm text-surface-400 mb-4">
                        Choose your preferred MFA method:
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 p-3 rounded-lg bg-surface-700/30 cursor-pointer hover:bg-surface-700/50 transition-colors">
                          <input
                            type="radio"
                            name="mfa-method"
                            value="totp"
                            checked={mfaMethod === "totp"}
                            onChange={() => setMfaMethod("totp")}
                            className="w-4 h-4 text-primary-500"
                          />
                          <div>
                            <p className="font-medium text-surface-100">
                              Authenticator App (TOTP)
                            </p>
                            <p className="text-xs text-surface-400">
                              Use Google/Microsoft Authenticator
                            </p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 p-3 rounded-lg bg-surface-700/30 cursor-pointer hover:bg-surface-700/50 transition-colors">
                          <input
                            type="radio"
                            name="mfa-method"
                            value="email"
                            checked={mfaMethod === "email"}
                            onChange={() => setMfaMethod("email")}
                            className="w-4 h-4 text-primary-500"
                          />
                          <div>
                            <p className="font-medium text-surface-100">
                              Email OTP
                            </p>
                            <p className="text-xs text-surface-400">
                              Receive code via email
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowMFASetup(false)}
                        className="flex-1 py-3 px-4 bg-surface-700 hover:bg-surface-600 text-surface-100 rounded-xl font-semibold transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleEnableMFA}
                        disabled={isLoading}
                        className="flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-all disabled:opacity-50"
                      >
                        {isLoading ? "Enabling..." : "Enable"}
                      </button>
                    </div>
                  </div>
                )
              ) : // MFA enabled - show disable option
              !showDisableConfirm ? (
                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                    <p className="text-sm text-green-400">
                      ✓ MFA is enabled using{" "}
                      <strong>
                        {user.mfa_method === "totp"
                          ? "Authenticator App"
                          : "Email OTP"}
                      </strong>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowRegenerateConfirm(true)}
                      className="flex-1 py-3 px-4 bg-surface-700 hover:bg-surface-600 text-surface-100 border border-surface-600/50 rounded-xl font-semibold transition-all"
                    >
                      New Backup Codes
                    </button>
                    <button
                      onClick={() => setShowDisableConfirm(true)}
                      className="flex-1 py-3 px-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-xl font-semibold transition-all"
                    >
                      Disable MFA
                    </button>
                  </div>
                </div>
              ) : (
                // Disable confirmation
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                    <p className="text-sm text-red-400">
                      ⚠ Disabling MFA will make your account less secure. Enter
                      your password to confirm.
                    </p>
                  </div>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 bg-surface-800/50 border border-surface-700/50 rounded-xl text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDisableConfirm(false);
                        setDisablePassword("");
                      }}
                      className="flex-1 py-3 px-4 bg-surface-700 hover:bg-surface-600 text-surface-100 rounded-xl font-semibold transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDisableMFA}
                      disabled={isLoading || !disablePassword}
                      className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-all disabled:opacity-50"
                    >
                      {isLoading ? "Disabling..." : "Disable MFA"}
                    </button>
                  </div>
                </div>
              )}

              {/* Regenerate Backup Codes Confirmation */}
              {showRegenerateConfirm && (
                <div className="mt-4 p-4 rounded-xl bg-surface-800/50 border border-yellow-500/30">
                  <p className="text-sm text-yellow-400 mb-3">
                    ⚠ This will generate new backup codes. Your old codes will
                    no longer work.
                  </p>
                  <input
                    type="password"
                    value={regeneratePassword}
                    onChange={(e) => setRegeneratePassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 bg-surface-800/50 border border-surface-700/50 rounded-xl text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 mb-3"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowRegenerateConfirm(false);
                        setRegeneratePassword("");
                      }}
                      className="flex-1 py-3 px-4 bg-surface-700 hover:bg-surface-600 text-surface-100 rounded-xl font-semibold transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRegenerateBackupCodes}
                      disabled={isLoading || !regeneratePassword}
                      className="flex-1 py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-black rounded-xl font-semibold transition-all disabled:opacity-50"
                    >
                      {isLoading ? "Generating..." : "Generate New Codes"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Backup Codes Modal */}
            {showBackupCodes && mfaData && (
              <div className="mt-6 p-4 rounded-xl bg-surface-800/50 border border-surface-700/50">
                <h4 className="font-semibold text-surface-100 mb-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-yellow-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Save Your Backup Codes
                </h4>
                <p className="text-sm text-surface-400 mb-4">
                  Store these codes in a safe place. Each code can only be used
                  once.
                </p>

                {/* QR Code for TOTP */}
                {mfaMethod === "totp" && mfaData.qr_code && (
                  <div className="mb-4 p-4 bg-white rounded-lg flex flex-col items-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaData.qr_code)}`}
                      alt="TOTP QR Code"
                      className="w-48 h-48"
                    />
                    <p className="text-xs text-gray-600 mt-2 text-center">
                      Scan with your authenticator app
                    </p>
                    {mfaData.secret && (
                      <button
                        onClick={() => copyToClipboard(mfaData.secret!)}
                        className="mt-2 text-xs text-primary-600 hover:underline"
                      >
                        Or copy secret: {mfaData.secret.substring(0, 8)}...
                      </button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mb-4">
                  {mfaData.backup_codes.map((code, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 bg-surface-900 rounded-lg text-center font-mono text-sm text-surface-200"
                    >
                      {code}
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() =>
                      copyToClipboard(mfaData.backup_codes.join("\n"))
                    }
                    className="flex-1 py-2 px-4 bg-surface-700 hover:bg-surface-600 text-surface-100 rounded-lg text-sm font-medium transition-all"
                  >
                    Copy All
                  </button>
                  <button
                    onClick={() => setShowBackupCodes(false)}
                    className="flex-1 py-2 px-4 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium transition-all"
                  >
                    I've Saved Them
                  </button>
                </div>
              </div>
            )}

            {/* Back to app link */}
            <div className="mt-8 text-center">
              <Link
                to="/search"
                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}

export default ProfilePage;
